import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  destination_country: "",
  categories: [],
  volume: "",
  branding: "",
};

const CATEGORY_OPTIONS = [
  "Ripple Wall Cups",
  "Double Wall Cups",
  "Embossed Collection",
  "Single Wall (Hot / Cold)",
  "Specialty & Pint Cups",
  "Lids, Straws & Accessories",
];

export default function SampleKitForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleCategory = (cat) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(cat)
        ? f.categories.filter((c) => c !== cat)
        : [...f.categories, cat],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.destination_country) {
      toast.error("Please fill in name, email and destination country.");
      return;
    }
    if (form.categories.length === 0) {
      toast.error("Please select at least one category to sample.");
      return;
    }
    setLoading(true);
    try {
      const messageParts = [
        `Sample Kit Request — Eco-Packaging`,
        ``,
        `Categories requested: ${form.categories.join(", ")}`,
        `Estimated annual volume: ${form.volume || "Not specified"}`,
        `Branding requirement: ${form.branding || "Standard / unbranded samples"}`,
      ];
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        product_interest: "Eco Packaging — Sample Kit",
        quantity: form.volume,
        destination_country: form.destination_country,
        message: messageParts.join("\n"),
      });
      toast.success("Sample kit request received. Our team will confirm dispatch within 24 hours.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please email contact@aadlabsglobal.com directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 bg-white border border-neutral-300 rounded-md text-brand-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition";

  return (
    <form
      data-testid="sample-kit-form"
      onSubmit={submit}
      className="bg-white rounded-lg p-6 md:p-8 shadow-md border border-neutral-200 space-y-5"
    >
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Full Name *</label>
          <input
            data-testid="samplekit-input-name"
            required
            value={form.name}
            onChange={update("name")}
            className={`${inputCls} mt-1.5`}
            placeholder="Your full name"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Company / Group</label>
          <input
            data-testid="samplekit-input-company"
            value={form.company}
            onChange={update("company")}
            className={`${inputCls} mt-1.5`}
            placeholder="Café · Hotel Group · Distributor"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Business Email *</label>
          <input
            data-testid="samplekit-input-email"
            required
            type="email"
            value={form.email}
            onChange={update("email")}
            className={`${inputCls} mt-1.5`}
            placeholder="you@company.com"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Phone / WhatsApp</label>
          <input
            data-testid="samplekit-input-phone"
            value={form.phone}
            onChange={update("phone")}
            className={`${inputCls} mt-1.5`}
            placeholder="+1 555 000 0000"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Ship Sample Kit To (Country) *</label>
        <input
          data-testid="samplekit-input-country"
          required
          value={form.destination_country}
          onChange={update("destination_country")}
          className={`${inputCls} mt-1.5`}
          placeholder="e.g., United Kingdom"
        />
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">
          Categories to Sample <span className="text-brand-gold">*</span>
        </label>
        <div className="mt-2 grid sm:grid-cols-2 gap-2">
          {CATEGORY_OPTIONS.map((cat) => {
            const active = form.categories.includes(cat);
            return (
              <button
                type="button"
                key={cat}
                data-testid={`samplekit-cat-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => toggleCategory(cat)}
                className={`text-left px-4 py-3 rounded-md border text-sm transition ${
                  active
                    ? "border-brand-gold bg-[#FFF8E6] text-brand-charcoal font-semibold"
                    : "border-neutral-300 bg-white text-brand-charcoal/80 hover:border-brand-gold hover:text-brand-charcoal"
                }`}
              >
                <span className="inline-block w-4 h-4 rounded-sm mr-2 align-middle border border-brand-green"
                  style={{ background: active ? "#C9A227" : "transparent" }} />
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Estimated Annual Volume</label>
          <input
            data-testid="samplekit-input-volume"
            value={form.volume}
            onChange={update("volume")}
            className={`${inputCls} mt-1.5`}
            placeholder="e.g., 500k cups / year"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Branding Requirement</label>
          <select
            data-testid="samplekit-select-branding"
            value={form.branding}
            onChange={update("branding")}
            className={`${inputCls} mt-1.5`}
          >
            <option value="">Select</option>
            <option value="Unbranded / Stock">Unbranded / Stock samples</option>
            <option value="Custom print interest">Interested in custom print</option>
            <option value="Custom embossing interest">Interested in custom embossing</option>
            <option value="Full private label">Full private-label programme</option>
          </select>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="samplekit-submit-button"
        className="btn-gold w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? "Submitting..." : (
          <>
            <Package className="w-4 h-4" />
            Request Free Sample Kit
          </>
        )}
      </button>

      <p className="text-xs text-neutral-500 text-center">
        Sample kits are complimentary for qualified B2B buyers. Freight billed at cost for destinations outside India. Confidential — never resold or shared.
      </p>
    </form>
  );
}
